import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellRing, FileText } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import {
  DataTable,
  type DataTableColumn,
  CustomizableMetrics,
  MetricTile,
  StatusPill,
  type StatusTone,
  WorkspacePage,
  useContextMenu,
  type ContextMenuItem,
} from '@forq/ui';
import { emptyCompanyProfile, type CompanyProfileGateway, type DunningLevelSettings, type TenantCompanyProfile } from '@forq/platform-data';
import type { CustomersGateway } from '@forq/customers-data';
import { FinanceNav } from './FinanceNav';
import { renderDunningHtml } from './dunningDocument';
import {
  addDays,
  daysOverdue,
  dunningLevelLabel,
  MAX_DUNNING_LEVEL,
  openAmount,
  todayIso,
  verzugszinsen,
  verzugszinssatz,
  type FinanceGateway,
  type Invoice,
  type InvoiceDunning,
} from '@forq/finance-data';

interface MahnwesenPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly financeGateway: FinanceGateway;
  /** Für Briefkopf, Mahnstufen und den Basiszinssatz. */
  readonly companyGateway: CompanyProfileGateway;
  /** Für die Anschrift im Mahnbrief. */
  readonly customersGateway: CustomersGateway;
}

/** Ein offener Mahnbrief, bereit zum Drucken. */
interface LetterState {
  readonly invoice: Invoice;
  readonly dunning: InvoiceDunning;
  readonly level: DunningLevelSettings;
}

function money(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}
function dateLabel(value: string | null): string {
  return value ? new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`)) : '–';
}
function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Mahnlauf konnte nicht verarbeitet werden.';
}

const levelTone: readonly StatusTone[] = ['neutral', 'info', 'warning', 'danger'];

export function MahnwesenPage({ tenant, mode, permissions, financeGateway, companyGateway, customersGateway }: MahnwesenPageProps) {
  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [company, setCompany] = useState<TenantCompanyProfile>(emptyCompanyProfile);
  const [letter, setLetter] = useState<LetterState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const canDun = permissions.has('finance.invoices.edit');

  useEffect(() => {
    void companyGateway.loadProfile(tenant.id).then(setCompany).catch(() => setCompany(emptyCompanyProfile));
  }, [companyGateway, tenant.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setInvoices(await financeGateway.listInvoices({ tenantId: tenant.id }));
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const overdue = useMemo(
    () => invoices
      .filter((i) => (i.paymentStatus === 'open' || i.paymentStatus === 'partial') && daysOverdue(i, today) > 0)
      .slice()
      .sort((a, b) => daysOverdue(b, today) - daysOverdue(a, today)),
    [invoices, today],
  );

  const metrics = useMemo(() => ({
    overdueSum: overdue.reduce((s, i) => s + openAmount(i), 0),
    count: overdue.length,
    dunned: overdue.filter((i) => i.dunningLevel > 0).length,
    // Die tatsaechlich AUSGESPROCHENEN Gebuehren, nicht ein Anzeigewert aus einer Tabelle.
    fees: overdue.reduce((s, i) => s + i.dunnings.reduce((f2, d) => f2 + d.fee, 0), 0),
  }), [overdue]);

  /**
   * Die naechste Mahnstufe aussprechen -- mit der Gebuehr und dem Text, die der MANDANT
   * hinterlegt hat, und einem Zinsvorschlag nach § 288 BGB.
   *
   * Der Zinssatz haengt am Basiszinssatz aus der Grundkonfiguration. Ist er nicht gepflegt,
   * werden bewusst KEINE Zinsen vorgeschlagen -- ein geratener Zinssatz auf einem Mahnschreiben
   * ist schlimmer als gar keiner.
   */
  const dun = useCallback(async (invoice: Invoice) => {
    const stufe = company.dunning.levels[invoice.dunningLevel];
    if (!stufe) { setError('Für diese Mahnstufe ist im Firmenprofil nichts hinterlegt.'); return; }
    const faellig = addDays(today, stufe.dueDays);
    const zinsen = company.dunning.chargeInterest && company.dunning.baseInterestRate !== 0 && invoice.dueDate
      ? verzugszinsen(
          openAmount(invoice),
          // Ohne Kundenart auf der Rechnung gilt der B2B-Satz -- FTW verkauft ganz ueberwiegend
          // an Unternehmen, und der niedrigere Satz waere die riskantere Annahme.
          verzugszinssatz(company.dunning.baseInterestRate, true),
          invoice.dueDate,
          today,
        )
      : 0;
    setBusy(true);
    setError(null);
    try {
      const updated = await financeGateway.dunInvoice({
        tenantId: tenant.id, invoiceId: invoice.id,
        fee: stufe.fee, interest: zinsen, dueDate: faellig,
      });
      setInvoices((current) => current.map((i) => i.id === updated.id ? updated : i));
      // Direkt den Brief oeffnen -- eine Mahnung, die niemandem zugeht, setzt niemanden in Verzug.
      const vorgang = updated.dunnings[updated.dunnings.length - 1];
      if (vorgang) setLetter({ invoice: updated, dunning: vorgang, level: stufe });
    } catch (dunError) {
      setError(errorMessage(dunError));
    } finally {
      setBusy(false);
    }
  }, [financeGateway, tenant.id, company, today]);

  const { open: openDunMenu, menu: dunMenu } = useContextMenu();
  const dunMenuItems = useCallback((i: Invoice): ContextMenuItem[] => (
    canDun && i.dunningLevel < MAX_DUNNING_LEVEL
      ? [{ label: i.dunningLevel === 0 ? 'Zahlungserinnerung senden' : 'Nächste Mahnstufe', icon: <BellRing size={15} />, disabled: busy, onSelect: () => void dun(i) }]
      : []
  ), [canDun, busy, dun]);

  const columns: readonly DataTableColumn<Invoice>[] = [
    { key: 'number', header: 'Rechnung', width: 'minmax(0, 1fr)', render: (i) => <span className="forq-stack"><strong>{i.invoiceNumber ?? i.proposalNumber}</strong><small>{i.customerName}</small></span> },
    { key: 'open', header: 'Offen', width: '110px', align: 'end', render: (i) => <span className="forq-cell-num">{money(openAmount(i))}</span> },
    { key: 'due', header: 'Fällig', width: '160px', render: (i) => <span className="finance-overdue">{dateLabel(i.dueDate)} · {daysOverdue(i, today)} T. über</span> },
    {
      key: 'level', header: 'Mahnstufe',
      render: (i) => <StatusPill label={dunningLevelLabel(i.dunningLevel)} tone={levelTone[i.dunningLevel] ?? 'danger'} />,
    },
    {
      key: 'fee', header: 'Gebühr', width: '90px', align: 'end',
      render: (i) => {
        const summe = i.dunnings.reduce((s, d) => s + d.fee + d.interest, 0);
        return <span className="forq-cell-num">{summe > 0 ? money(summe) : '–'}</span>;
      },
    },
    {
      key: 'action', header: '', width: '150px', align: 'end',
      render: (i) => {
        const letzte = i.dunnings[i.dunnings.length - 1];
        const briefKnopf = letzte ? (
          <button type="button" className="finance-actions__btn" onClick={() => {
            const stufe = company.dunning.levels[letzte.level - 1];
            if (stufe) setLetter({ invoice: i, dunning: letzte, level: stufe });
          }}><FileText size={13} /> Brief</button>
        ) : null;
        if (!canDun) return briefKnopf;
        return (
          <span className="forq-cell-actions">
            {briefKnopf}
            {i.dunningLevel >= MAX_DUNNING_LEVEL
              ? <span className="finance-overdue">höchste Stufe</span>
              : <button type="button" className="finance-actions__btn is-danger" disabled={busy} onClick={() => void dun(i)}><BellRing size={13} /> {i.dunningLevel === 0 ? 'Erinnern' : 'Nächste Mahnung'}</button>}
          </span>
        );
      },
    },
  ];

  return (
    <WorkspacePage
      customizeKey="finance-mahnwesen"
      eyebrow={`FORQ Finance · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Mahnwesen"
      description="Überfällige Forderungen stufenweise anmahnen – Zahlungserinnerung, 1. und 2. Mahnung mit Gebühren."
      metrics={(
        <CustomizableMetrics
          pageKey="finance-mahnwesen"
          tiles={[
            { key: 'overdue-sum', node: <MetricTile label="Überfällig gesamt" value={money(metrics.overdueSum)} hint={`${metrics.count} Forderungen`} tone={metrics.count > 0 ? 'danger' : 'default'} /> },
            { key: 'dunned', node: <MetricTile label="Bereits gemahnt" value={metrics.dunned} hint="mind. Stufe 1" /> },
            { key: 'fees', node: <MetricTile label="Mahngebühren" value={money(metrics.fees)} hint="aufgelaufen" tone="warning" /> },
            { key: 'max-level', node: <MetricTile label="Höchste Stufe" value={dunningLevelLabel(overdue.reduce((m, i) => Math.max(m, i.dunningLevel), 0))} hint="im Bestand" /> },
          ]}
        />
      )}
      toolbar={<div className="finance-toolbar"><FinanceNav /></div>}
    >
      {error && <div className="finance-page__error">{error}</div>}
      <DataTable
        columns={columns}
        rows={overdue}
        rowKey={(i) => i.id}
        rowTone={() => 'danger'}
        onRowContext={(event, i) => openDunMenu(event, dunMenuItems(i))}
        loading={loading}
        loadingLabel="Offene Posten werden geprüft …"
        emptyLabel="Keine überfälligen Forderungen – alles im grünen Bereich."
        ariaLabel="Überfällige Forderungen"
      />
      {dunMenu}
      {letter && (
        <DunningLetterModal
          state={letter}
          company={company}
          tenantId={tenant.id}
          customersGateway={customersGateway}
          onClose={() => setLetter(null)}
        />
      )}
    </WorkspacePage>
  );
}

/**
 * Der Mahnbrief zum Drucken. Die Anschrift kommt aus dem Kundenstamm; findet FORQ den Kunden
 * nicht, steht wenigstens der Name auf dem Brief -- ein Schreiben ohne Anschrift ist immer noch
 * besser als gar keins, und der Anwender sieht sofort, was fehlt.
 */
function DunningLetterModal({ state, company, tenantId, customersGateway, onClose }: {
  readonly state: LetterState;
  readonly company: TenantCompanyProfile;
  readonly tenantId: string;
  readonly customersGateway: CustomersGateway;
  readonly onClose: () => void;
}) {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const { invoice, dunning, level } = state;
      let recipient = {
        name: invoice.customerName, street: '', postalCode: '', city: '', countryCode: 'DE',
      };
      try {
        const page = await customersGateway.searchCustomers({ tenantId, query: invoice.customerNumber, pageSize: 5 });
        const treffer = page.items.find((item) => item.number === invoice.customerNumber);
        if (treffer) {
          const kunde = await customersGateway.getCustomer(tenantId, treffer.id);
          const adresse = kunde.addresses.find((a) => a.kind === 'billing')
            ?? kunde.addresses.find((a) => a.primary)
            ?? kunde.addresses[0];
          recipient = {
            name: kunde.displayName,
            street: adresse?.street ?? '',
            postalCode: adresse?.postalCode ?? '',
            city: adresse?.city ?? '',
            countryCode: adresse?.countryCode || 'DE',
          };
        }
      } catch { /* Ohne Stammtreffer bleibt der Name aus der Rechnung. */ }
      if (active) setHtml(renderDunningHtml({ invoice, dunning, level, company, recipient }));
    })();
    return () => { active = false; };
  }, [state, company, tenantId, customersGateway]);

  const nummer = state.invoice.invoiceNumber ?? state.invoice.proposalNumber;
  return (
    <div className="orders-doc-modal" role="dialog" aria-modal="true" aria-label={`${state.level.label} ${nummer}`}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="orders-doc-modal__panel">
        <header>
          <span>{state.level.label} · {nummer}</span>
          <button type="button" className="orders-doc-modal__close" onClick={onClose}>Schließen</button>
        </header>
        {html
          ? <iframe className="orders-doc-frame" title={`${state.level.label} ${nummer}`} srcDoc={html} />
          : <p className="finance-work__empty">Brief wird aufgebaut …</p>}
      </div>
    </div>
  );
}
